import { useState, useEffect } from 'react';
import { Zap, Award, Sparkles, CheckCircle, Star, X, CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = `${window.location.origin}/api`;
const MELANIN_ICON = "https://customer-assets.emergentagent.com/job_blackownedhub/artifacts/9kb68tzi_Melanin%20Icon%20-%20White%20with%20Black%20Background.png";

const BOOST_OPTIONS = [
  {
    type: 'featured_7d',
    name: '7-Day Boost',
    description: 'Featured on homepage for 1 week',
    price: 2.00,
    icon: Zap,
    color: 'blue'
  },
  {
    type: 'featured_30d',
    name: '30-Day Boost',
    description: 'Featured on homepage for 1 month',
    price: 5.00,
    icon: Sparkles,
    color: 'purple',
    popular: true
  }
];

const BADGE_OPTIONS = [
  {
    type: 'verified_plus',
    name: 'Verified Plus',
    description: 'Enhanced verification badge',
    price: 3.00,
    icon: CheckCircle,
    gradient: 'from-green-400 to-emerald-600'
  },
  {
    type: 'top_rated',
    name: 'Top Rated',
    description: 'For highly-rated businesses',
    price: 5.00,
    icon: Star,
    gradient: 'from-yellow-400 to-orange-500'
  },
  {
    type: 'community_choice',
    name: 'Community Choice',
    description: 'Community favorite award',
    price: 10.00,
    icon: Award,
    gradient: 'from-pink-400 to-purple-600'
  }
];

export default function BoostBadgeDialog({ 
  open, 
  onOpenChange, 
  business, 
  walletBalance, 
  onPurchaseComplete 
}) {
  const [activeTab, setActiveTab] = useState('boost');
  const [purchasing, setPurchasing] = useState(false);
  const [activeBoost, setActiveBoost] = useState(null);
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    const fetchBusinessStatus = async () => {
      try {
        // Check for active boost
        const boostRes = await fetch(`${API}/businesses/${business.business_id}/boost`, {
          credentials: 'include'
        });
        if (boostRes.ok) {
          const boostData = await boostRes.json();
          setActiveBoost(boostData.active_boost);
        }

        // Check for badges
        const badgeRes = await fetch(`${API}/businesses/${business.business_id}/badges`, {
          credentials: 'include'
        });
        if (badgeRes.ok) {
          const badgeData = await badgeRes.json();
          setBadges(badgeData);
        }
      } catch (error) {
        console.error('Error fetching business status:', error);
      }
    };

    if (open && business) {
      fetchBusinessStatus();
    }
  }, [open, business]);

  const handlePurchaseBoost = async (boostType) => {
    if (activeBoost) {
      toast.error('This business already has an active boost');
      return;
    }

    const option = BOOST_OPTIONS.find(o => o.type === boostType);
    if (walletBalance < option.price) {
      toast.error(`Insufficient balance. You need ${option.price.toFixed(2)} MEL`);
      return;
    }

    setPurchasing(true);
    try {
      const response = await fetch(`${API}/shop/boost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          business_id: business.business_id,
          boost_type: boostType
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${option.name} purchased! Your business is now featured.`);
        setActiveBoost({ boost_type: boostType, expires_at: data.expires_at });
        onPurchaseComplete?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to purchase boost');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setPurchasing(false);
    }
  };

  const handlePurchaseBadge = async (badgeType) => {
    const hasBadge = badges.some(b => b.badge_type === badgeType);
    if (hasBadge) {
      toast.error('You already have this badge');
      return;
    }

    const option = BADGE_OPTIONS.find(o => o.type === badgeType);
    if (walletBalance < option.price) {
      toast.error(`Insufficient balance. You need ${option.price.toFixed(2)} MEL`);
      return;
    }

    setPurchasing(true);
    try {
      const response = await fetch(`${API}/shop/badge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          business_id: business.business_id,
          badge_type: badgeType
        })
      });

      if (response.ok) {
        toast.success(`${option.name} badge purchased!`);
        setBadges([...badges, { badge_type: badgeType }]);
        onPurchaseComplete?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to purchase badge');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setPurchasing(false);
    }
  };

  if (!business) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border border-zinc-800 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-purple-400" />
            Boost & Badges
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Promote <span className="text-white font-medium">{business.name}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Wallet Balance */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-2">
            <img src={MELANIN_ICON} alt="Melanin" className="w-5 h-5" />
            <span className="text-zinc-400 text-sm">Your Balance</span>
          </div>
          <span className={`text-xl font-bold ${walletBalance < 2 ? 'text-red-400' : 'text-white'}`}>
            {walletBalance.toFixed(2)} MEL
          </span>
        </div>

        {/* Low Balance Warning & Buy MEL Link */}
        {walletBalance < 5 && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400/90 text-sm">Need more MEL?</span>
            </div>
            <Link
              to="/dashboard?tab=wallet"
              onClick={() => onOpenChange(false)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-400 text-sm font-medium hover:bg-amber-500/30 transition-colors"
              data-testid="buy-mel-link-badge"
            >
              <Sparkles className="w-3 h-3" />
              Buy MEL
            </Link>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-zinc-800 pb-3">
          <button
            onClick={() => setActiveTab('boost')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
              activeTab === 'boost'
                ? 'bg-white text-black'
                : 'bg-zinc-900 text-zinc-400 hover:text-white'
            }`}
            data-testid="boost-tab"
          >
            <Zap className="w-4 h-4" />
            Boosts
          </button>
          <button
            onClick={() => setActiveTab('badge')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
              activeTab === 'badge'
                ? 'bg-white text-black'
                : 'bg-zinc-900 text-zinc-400 hover:text-white'
            }`}
            data-testid="badge-tab"
          >
            <Award className="w-4 h-4" />
            Badges
          </button>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'boost' ? (
            <motion.div
              key="boost"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="space-y-3"
            >
              {activeBoost && (
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Active boost expires {new Date(activeBoost.expires_at).toLocaleDateString()}
                </div>
              )}

              {BOOST_OPTIONS.map((option) => {
                const Icon = option.icon;
                const canAfford = walletBalance >= option.price;
                const isActive = activeBoost?.boost_type === option.type;

                return (
                  <div
                    key={option.type}
                    className={`relative p-4 rounded-xl border transition-all ${
                      option.color === 'blue'
                        ? 'bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20'
                        : 'bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20'
                    }`}
                  >
                    {option.popular && (
                      <span className="absolute -top-2 right-3 px-2 py-0.5 text-xs font-medium bg-purple-500 text-white rounded-full">
                        Best Value
                      </span>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          option.color === 'blue' ? 'bg-blue-500/20' : 'bg-purple-500/20'
                        }`}>
                          <Icon className={`w-5 h-5 ${
                            option.color === 'blue' ? 'text-blue-400' : 'text-purple-400'
                          }`} />
                        </div>
                        <div>
                          <h4 className="font-medium text-white">{option.name}</h4>
                          <p className="text-zinc-500 text-xs">{option.description}</p>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="flex items-center gap-1 mb-1">
                          <img src={MELANIN_ICON} alt="" className="w-4 h-4" />
                          <span className="font-bold text-white">{option.price.toFixed(2)}</span>
                          <span className="text-zinc-500 text-sm">MEL</span>
                        </div>
                        <button
                          onClick={() => handlePurchaseBoost(option.type)}
                          disabled={purchasing || !canAfford || !!activeBoost}
                          className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                            isActive
                              ? 'bg-green-500/20 text-green-400 cursor-default'
                              : !canAfford || !!activeBoost
                              ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                              : 'bg-white text-black hover:bg-zinc-200'
                          }`}
                          data-testid={`purchase-boost-${option.type}`}
                        >
                          {isActive ? 'Active' : activeBoost ? 'Has Boost' : !canAfford ? 'Insufficient' : 'Purchase'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              key="badge"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="space-y-3"
            >
              {BADGE_OPTIONS.map((option) => {
                const Icon = option.icon;
                const canAfford = walletBalance >= option.price;
                const hasBadge = badges.some(b => b.badge_type === option.type);

                return (
                  <div
                    key={option.type}
                    className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${option.gradient} flex items-center justify-center`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-medium text-white">{option.name}</h4>
                          <p className="text-zinc-500 text-xs">{option.description}</p>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="flex items-center gap-1 mb-1">
                          <img src={MELANIN_ICON} alt="" className="w-4 h-4" />
                          <span className="font-bold text-white">{option.price.toFixed(2)}</span>
                          <span className="text-zinc-500 text-sm">MEL</span>
                        </div>
                        <button
                          onClick={() => handlePurchaseBadge(option.type)}
                          disabled={purchasing || !canAfford || hasBadge}
                          className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                            hasBadge
                              ? 'bg-green-500/20 text-green-400 cursor-default'
                              : !canAfford
                              ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                              : 'bg-white text-black hover:bg-zinc-200'
                          }`}
                          data-testid={`purchase-badge-${option.type}`}
                        >
                          {hasBadge ? 'Owned' : !canAfford ? 'Insufficient' : 'Purchase'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer Note */}
        <p className="text-zinc-600 text-xs text-center pt-2">
          Purchases are non-refundable. Earn more Melanin by submitting businesses or writing reviews.
        </p>
      </DialogContent>
    </Dialog>
  );
}
